/* 
  File: src/pages/stb/StbVerwaltung.js
  Name: StbVerwaltung.js
  Zweck: Anzeigenverwaltung – Übersicht aller Stellenanzeigen in tabellarischer Form.
         Jede Anzeige wird mit den Schaltern "Edit", "Löschen" und "Pausieren" versehen.
         Falls noch keine Anzeige angelegt wurde, wird dies entsprechend angezeigt.
         Der "Edit"-Button führt zur Bearbeitungsmaske (StbEditStellenanzeige).
         Am Ende gibt es einen "Zurück"-Button ins Hauptmenü.
*/

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";

const StbVerwaltung = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Lade alle Stellenanzeigen
  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/api/stb/employer/listings`)
      .then(response => {
        // Prüfen, ob der Rückgabewert ein Array ist
        const data = Array.isArray(response.data) ? response.data : [];
        setListings(data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Fehler beim Laden der Anzeigen:", err);
        setError("Fehler beim Laden der Anzeigen.");
        setLoading(false);
      });
  }, []);

  // Handler: Anzeige bearbeiten (führt zur Bearbeitungsmaske, die in StbEditStellenanzeige implementiert ist)
  const handleEdit = (id) => {
    navigate(`/dashboard/stb/edit/${id}`);
  };

  // Handler: Anzeige löschen
  const handleDelete = async (id) => {
    if (window.confirm("Soll diese Anzeige wirklich gelöscht werden?")) {
      try {
        await axios.delete(`${config.API_BASE_URL}/api/stb/suchanzeigen/${id}`);
        setListings(prev => prev.filter(listing => listing.id !== id));
      } catch (err) {
        console.error("Fehler beim Löschen der Anzeige:", err);
        alert("Fehler beim Löschen der Anzeige.");
      }
    }
  };

  // Handler: Anzeige pausieren (Status auf "paused" setzen)
  const handlePause = async (id) => {
    if (window.confirm("Soll diese Anzeige pausiert werden?")) {
      try {
        await axios.put(`${config.API_BASE_URL}/api/stb/suchanzeigen/${id}/pause`);
        setListings(prev =>
          prev.map(listing =>
            listing.id === id ? { ...listing, status: "paused" } : listing
          )
        );
      } catch (err) {
        console.error("Fehler beim Pausieren der Anzeige:", err);
        alert("Fehler beim Pausieren der Anzeige.");
      }
    }
  };

  if (loading) return <div className="text-white text-center">Lädt...</div>;
  if (error) return <div className="text-white text-center">{error}</div>;

  return (
    <div className="min-h-screen flex flex-col items-center p-5 bg-gray-900">
      <div className="bg-black bg-opacity-50 p-8 rounded-lg shadow-lg w-full max-w-6xl text-white">
        <h2 className="text-3xl font-bold mb-6 text-center">Anzeigenverwaltung</h2>
        <table className="min-w-full bg-gray-800 rounded-lg overflow-hidden">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-2 border">ID</th>
              <th className="px-4 py-2 border">Titel</th>
              <th className="px-4 py-2 border">Status</th>
              <th className="px-4 py-2 border">Erstellt am</th>
              <th className="px-4 py-2 border">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {listings.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-2 text-center">
                  Noch keine Stellenanzeigen angelegt
                </td>
              </tr>
            ) : (
              listings.map(listing => (
                <tr key={listing.id} className="border-t text-center">
                  <td className="px-4 py-2 border">{listing.id}</td>
                  <td className="px-4 py-2 border">{listing.title}</td>
                  <td className="px-4 py-2 border">{listing.status}</td>
                  <td className="px-4 py-2 border">{new Date(listing.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2 border space-x-2">
                    <button
                      onClick={() => handleEdit(listing.id)}
                      className="bg-blue-500 hover:bg-blue-600 px-3 py-1 rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(listing.id)}
                      className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded"
                    >
                      Löschen
                    </button>
                    <button
                      onClick={() => handlePause(listing.id)}
                      className="bg-yellow-500 hover:bg-yellow-600 px-3 py-1 rounded"
                    >
                      Pausieren
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="mt-6 text-center">
          <button 
            onClick={() => navigate("/dashboard/employer")}
            className="bg-gray-500 hover:bg-gray-600 px-6 py-2 rounded"
          >
            Zurück ins Hauptmenü
          </button>
        </div>
      </div>
    </div>
  );
};

export default StbVerwaltung;
